import type { AriaPopoverProps } from "@react-aria/overlays";
import { DismissButton, Overlay, usePopover } from "@react-aria/overlays";
import cn from "classnames";
import * as React from "react";
import type { OverlayTriggerState } from "react-stately";
import { useSessionGuard } from "../../contexts/SessionGuardProvider";

export interface Props extends Omit<AriaPopoverProps, "popoverRef"> {
  children: React.ReactNode;
  state: OverlayTriggerState;
  className?: string;
  popoverRef?: React.RefObject<HTMLDivElement | null>;
  parentContainerRef?: React.RefObject<HTMLDivElement | null>;
  /**
   * The container element in which the overlay portal will be placed.
   * @default document.body
   */
  portalContainer?: Element;
}

function Popover({
  portalContainer,
  ...props
}: Props & React.HtmlHTMLAttributes<HTMLDivElement>) {
  const ref = React.useRef<HTMLDivElement>(null);
  const {
    offset = 8,
    popoverRef = ref,
    state,
    children,
    className,
    isNonModal,
  } = props;

  const { reason: sessionGuardReason } = useSessionGuard();

  const { popoverProps, underlayProps } = usePopover(
    {
      ...props,
      popoverRef: popoverRef as React.RefObject<HTMLDivElement>,
      offset,
    },
    state,
  );

  React.useEffect(() => {
    if (sessionGuardReason) state.close();
  }, [sessionGuardReason, state]);

  if (props?.parentContainerRef?.current) {
    popoverProps.style = {
      ...popoverProps.style,
      width: props.parentContainerRef.current.clientWidth + "px",
    };
  }
  return (
    <Overlay portalContainer={portalContainer}>
      {!isNonModal && (
        <div {...underlayProps} className="ds-popover__underlay" />
      )}
      <div
        {...popoverProps}
        ref={popoverRef}
        className={cn("ds-popover", className)}
        style={{ ...popoverProps.style, ...props.style }}
      >
        {children}
        {!isNonModal && <DismissButton onDismiss={state.close} />}
      </div>
    </Overlay>
  );
}

export default Popover;
